import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMediaQuery } from '@/lib/hooks'
import { formatCount } from '@/lib/format'

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info']

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'var(--red)',       // #ef4444 - red
  high:     'var(--sev-high)',   // dark orange, deepened in light theme
  medium:   'var(--sev-medium)', // bright yellow in dark, deep amber-brown in light
  low:      'var(--blue)',       // #3b82f6 - blue
  info:     'var(--sev-info)',   // slate - clear of the purple accent
}

/** One linked artifact row in a donut segment's drill-down popover. */
export interface FindingsDrilldownItem {
  label: string
  href: string
  count: number
}

export interface FindingsDrilldown {
  severity: string
  total_artifacts: number
  items: FindingsDrilldownItem[]
}

export function FindingsChart({
  findings,
  drilldown,
  loading,
}: {
  findings: Array<{ severity: string; count: number }>
  /** Per-severity affected artifacts; hovering a segment (or its legend row) pops a panel of
   *  clickable links. Absent (older worker) => the ring behaves exactly as before. */
  drilldown?: FindingsDrilldown[]
  loading?: boolean
}) {
  const buckets = SEVERITY_ORDER.map((sev) => ({
    severity: sev,
    count: findings.find((f) => f.severity === sev)?.count ?? 0,
  })).filter((b) => b.count > 0)

  const total = buckets.reduce((s, b) => s + b.count, 0)

  const [hovered, setHoveredRaw] = useState<string | null>(null)
  // Grace-period hover: the drill-down panel must be reachable by mouse (its links are the
  // point), so leaving an arc/legend row only clears the hover after a beat - long enough to
  // travel into the panel, short enough that the ring never feels sticky.
  const leaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const enter = (sev: string) => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null }
    setHoveredRaw(sev)
  }
  const leave = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
    leaveTimer.current = setTimeout(() => setHoveredRaw(null), 150)
  }
  // Inline SVG transitions can't be reached by a CSS media query, so gate the
  // grow/glow animation on the reduced-motion preference here. The colour/opacity
  // still swaps instantly; only the tween is dropped.
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const arcTransition = reduceMotion ? 'none' : 'stroke-width 0.15s, opacity 0.15s, filter 0.15s'

  const R = 40
  const C = 2 * Math.PI * R
  // Small separation between slices so the ring reads as distinct wedges, not one
  // continuous band. A single-bucket donut skips the gap so it stays a full ring.
  const GAP = buckets.length > 1 ? 2.5 : 0
  let offset = 0
  const segments = buckets.map((b) => {
    const arc = (b.count / total) * C
    const dash = Math.max(arc - GAP, 0.5)
    const seg = { ...b, dash, gap: C - dash, offset }
    offset += arc
    return seg
  })

  const active = hovered ? buckets.find((b) => b.severity === hovered) : undefined

  if (loading) return <div style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '0.75rem' }}>-</div>

  if (buckets.length === 0) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontFamily: 'var(--head)', fontSize: '1.2rem', color: 'var(--green)' }}>✓</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)' }}>no findings</span>
    </div>
  )

  const activeDrill = hovered ? drilldown?.find((d) => d.severity === hovered) : undefined

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, position: 'relative' }}>
      {/* Drill-down popover: one panel whose content follows the hovered severity. It overlaps
          the donut (no mouse gap from the arcs) and is itself hover-held, so its links are
          clickable; legend-row hops are covered by the grace timer above. */}
      {activeDrill && activeDrill.items.length > 0 && (
        <div
          onMouseEnter={() => enter(activeDrill.severity)}
          onMouseLeave={leave}
          style={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '8px 10px',
            minWidth: 210,
            maxWidth: 280,
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: 2, background: SEVERITY_COLOR[activeDrill.severity] ?? 'var(--text2)', flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)' }}>
              {activeDrill.severity} · {formatCount(buckets.find((b) => b.severity === activeDrill.severity)?.count ?? 0)} finding{(buckets.find((b) => b.severity === activeDrill.severity)?.count ?? 0) === 1 ? '' : 's'}
            </span>
          </div>
          {activeDrill.items.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 4px', borderRadius: 4,
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg3)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.label}
              </span>
              <span style={{ fontFamily: 'var(--head)', fontSize: '0.72rem', color: 'var(--text2)', marginLeft: 'auto' }}>
                {formatCount(item.count)}
              </span>
            </Link>
          ))}
          {activeDrill.total_artifacts > activeDrill.items.length && (
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.6rem', color: 'var(--text3)', padding: '3px 4px' }}>
              + {activeDrill.total_artifacts - activeDrill.items.length} more
            </div>
          )}
        </div>
      )}
      <svg width={180} height={180} viewBox="0 0 100 100" style={{ flexShrink: 0, overflow: 'visible' }}>
        <circle cx={50} cy={50} r={R} fill="none" stroke="var(--bg3)" strokeWidth={14} />
        {segments.map(({ severity, dash, gap, offset: off }) => {
          const color = SEVERITY_COLOR[severity] ?? 'var(--text2)'
          const isHovered = hovered === severity
          const isDimmed = hovered !== null && !isHovered
          return (
            <circle
              key={severity}
              cx={50} cy={50} r={R}
              fill="none"
              stroke={color}
              strokeWidth={isHovered ? 17 : 14}
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-off}
              strokeLinecap="butt"
              transform="rotate(-90 50 50)"
              opacity={isDimmed ? 0.28 : 1}
              style={{
                filter: isHovered ? `drop-shadow(0 0 4px ${color})` : 'none',
                transition: arcTransition,
                cursor: 'default',
              }}
              onMouseEnter={() => enter(severity)}
              onMouseLeave={leave}
            />
          )
        })}
        <text x={50} y={48} textAnchor="middle" dominantBaseline="middle" fontFamily="var(--head)" fontSize={16} fontWeight={700} fill={active ? (SEVERITY_COLOR[active.severity] ?? 'var(--text)') : 'var(--text)'}>{formatCount(active ? active.count : total)}</text>
        <text x={50} y={60} textAnchor="middle" dominantBaseline="middle" fontFamily="var(--mono)" fontSize={6} fill="var(--text3)">{active ? active.severity.toUpperCase() : 'TOTAL'}</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
        {segments.map(({ severity, count }) => {
          const isHovered = hovered === severity
          const isDimmed = hovered !== null && !isHovered
          return (
            <button
              key={severity}
              type="button"
              onMouseEnter={() => enter(severity)}
              onMouseLeave={leave}
              onFocus={() => enter(severity)}
              onBlur={leave}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                textAlign: 'left',
                background: isHovered ? 'var(--bg3)' : 'transparent',
                border: 'none',
                borderRadius: 4,
                padding: '2px 6px',
                cursor: 'default',
                opacity: isDimmed ? 0.4 : 1,
                transition: arcTransition,
              }}
            >
              <div style={{ width: 7, height: 7, borderRadius: 2, background: SEVERITY_COLOR[severity] ?? 'var(--text2)', flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)' }}>{severity}</span>
              <span style={{ fontFamily: 'var(--head)', fontSize: '0.78rem', color: SEVERITY_COLOR[severity] ?? 'var(--text)', marginLeft: 'auto' }}>{formatCount(count)}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
